import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Chip } from '@mui/material';
import { LogoutRounded, BusinessRounded } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppStore';
import { setActingTenant } from '../../features/auth/authSlice';
import { baseApi } from '../../store/api/baseApi';
import { useExitTenantMutation } from '../../store/api/platformApi';

/** Alto de la barra — el layout la incluye en el topOffset del contenido/sidebar. */
export const TENANT_ACCESS_BAR_HEIGHT = 40;

/**
 * Banner de "modo soporte" para staff de plataforma (booster_admin / booster_support):
 * cuando entraste a un tenant (desde la consola /platform), avisa a qué tenant estás
 * viendo y ofrece "Salir". El scoping al tenant lo hace el header X-Acting-Tenant.
 * Solo se renderiza estando DENTRO de un tenant. Es `fixed` bajo el topbar (recibe
 * `top` del layout, que además le reserva el alto en el topOffset).
 */
export function TenantAccessBar({ top = 56 }: { top?: number }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const role   = useAppSelector((s) => s.auth.user?.role);
  const acting = useAppSelector((s) => s.auth.actingTenant);
  const [exit] = useExitTenantMutation();

  if (!acting) return null;

  const handleExit = async () => {
    await exit().unwrap().catch(() => {});
    dispatch(setActingTenant(null));
    dispatch(baseApi.util.resetApiState());
    navigate(role === 'booster_support' ? '/support' : '/platform'); // volver a su home de plataforma
  };

  return (
    <Box sx={{
      position: 'fixed', top, left: 0, right: 0,
      // Por encima del sidebar (drawer + 2, ver Sidebar.tsx): con 1200 le tapaba el
      // borde izquierdo al cartel. Mismo criterio que ImpersonationBanner. (03/09/2026)
      zIndex: (t: import('@mui/material').Theme) => t.zIndex.drawer + 3,
      height: TENANT_ACCESS_BAR_HEIGHT, display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5,
      background: 'linear-gradient(90deg, #451a03 0%, #78350f 50%, #451a03 100%)',
      color: '#fff',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
      boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
    }}>
      <BusinessRounded sx={{ fontSize: 18, opacity: 0.9 }} />
      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
        Estás viendo el tenant <strong>«{acting.name}»</strong>
      </Typography>
      <Chip size="small" label={acting.mode === 'booster' ? 'Booster' : 'Imperium'}
            sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: 'rgba(255,255,255,0.16)', color: '#fff', border: '1px solid rgba(255,255,255,0.22)' }} />
      <Typography variant="caption" sx={{ opacity: 0.75, display: { xs: 'none', sm: 'block' } }}>· acceso auditado</Typography>
      <Box sx={{ flex: 1 }} />
      <Button
        size="small" variant="contained" startIcon={<LogoutRounded sx={{ fontSize: '16px !important' }} />}
        onClick={handleExit}
        sx={{
          bgcolor: '#f59e0b', color: '#000', fontWeight: 900, py: 0.2,
          border: '1px solid rgba(245,158,11,0.5)', boxShadow: '0 2px 8px rgba(245,158,11,0.3)',
          '&:hover': { bgcolor: '#fbbf24', borderColor: '#fbbf24' },
        }}
      >
        Salir
      </Button>
    </Box>
  );
}
